import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createVideoConference } from '../../Requests/VideoConferenceRequests';
import { useTranslation } from 'react-i18next';

const ConferenceCreationModal = ({ handleClose, project = null, onSave }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        scheduled_for: '',
        settings: {
            max_participants: 10,
            enable_chat: true,
            enable_screen_share: true
        }
    });

    const [scheduleType, setScheduleType] = useState('now'); // 'now' or 'later'

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { t } = useTranslation();

    // Helper function to get default future date (1 hour from now)
    const getDefaultFutureDate = () => {
        const now = new Date();
        now.setHours(now.getHours() + 1);
        now.setMinutes(0);
        now.setSeconds(0);
        return now.toISOString().slice(0, 16); // Format for datetime-local input
    };

    const handleInputChange = (field, value) => {
        if (field.startsWith('settings.')) {
            const settingKey = field.replace('settings.', '');
            setFormData(prev => ({
                ...prev,
                settings: {
                    ...prev.settings,
                    [settingKey]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleScheduleTypeChange = (type) => {
        setScheduleType(type);
        if (type === 'now') {
            handleInputChange('scheduled_for', '');
        } else {
            handleInputChange('scheduled_for', getDefaultFutureDate());
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        
        try {
            const conferenceData = {
                title: formData.title,
                description: formData.description,
                settings: formData.settings,
                project_id: project?.id || null
            };

            // Only include scheduled_for if scheduling for later
            if (scheduleType === 'later' && formData.scheduled_for) {
                conferenceData.scheduled_for = formData.scheduled_for;
            }

            const response = await createVideoConference(conferenceData);
            
            // Debug logging to understand response structure
            // Conference creation response received
            
            // Handle different possible response structures
            const createdConference = response.data.data || response.data.conference || response.data;
            // Conference object parsed
            
            if (!createdConference || !createdConference.id) {
                throw new Error('Invalid response: missing conference data');
            }
            
            onSave(createdConference);
            handleClose();
        } catch (error) {
            console.error('Error creating video conference:', error);
            
            // Extract error message from response
            let errorMessage = 'Failed to create conference. Please try again.';
            
            if (error.response?.data?.errors) {
                // Handle validation errors
                const validationErrors = error.response.data.errors;
                const errorMessages = Object.values(validationErrors).flat();
                errorMessage = errorMessages.join(', ');
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={true} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                        <Dialog.Title className="text-lg font-medium">
                            {t('VideoConference.CreateModal.Title')}
                        </Dialog.Title>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Error Display */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                                <p className="text-sm">{error}</p>
                            </div>
                        )}

                        {/* Basic Information */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('VideoConference.CreateModal.ConferenceTitleLabel')}</label>
                            <input
                                type="text"
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                placeholder={t('VideoConference.CreateModal.ConferenceTitlePlaceholder')}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('VideoConference.CreateModal.DescriptionLabel')}</label>
                            <textarea
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder={t('VideoConference.CreateModal.DescriptionPlaceholder')}
                                rows={3}
                            />
                        </div>

                        {/* Schedule Type Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">{t('VideoConference.CreateModal.ScheduleQuestion')}</label>
                            <div className="space-y-3">
                                <div className="flex items-center">
                                    <input
                                        type="radio"
                                        id="schedule-now"
                                        name="schedule-type"
                                        className="h-4 w-4 border-gray-300 text-black focus:ring-black"
                                        checked={scheduleType === 'now'}
                                        onChange={() => handleScheduleTypeChange('now')}
                                    />
                                    <label htmlFor="schedule-now" className="ml-3 text-sm text-gray-700">
                                        <span className="font-medium">{t('VideoConference.CreateModal.StartNowOption')}</span>
                                        <span className="block text-xs text-gray-500">{t('VideoConference.CreateModal.StartNowDescription')}</span>
                                    </label>
                                </div>
                                
                                <div className="flex items-center">
                                    <input
                                        type="radio"
                                        id="schedule-later"
                                        name="schedule-type"
                                        className="h-4 w-4 border-gray-300 text-black focus:ring-black"
                                        checked={scheduleType === 'later'}
                                        onChange={() => handleScheduleTypeChange('later')}
                                    />
                                    <label htmlFor="schedule-later" className="ml-3 text-sm text-gray-700">
                                        <span className="font-medium">{t('VideoConference.CreateModal.ScheduleLaterOption')}</span>
                                        <span className="block text-xs text-gray-500">{t('VideoConference.CreateModal.ScheduleLaterDescription')}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Date/Time Picker (only shown when scheduling for later) */}
                        {scheduleType === 'later' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('VideoConference.CreateModal.DateTimeLabel')}</label>
                                <input
                                    type="datetime-local"
                                    required={scheduleType === 'later'}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                    value={formData.scheduled_for}
                                    onChange={(e) => handleInputChange('scheduled_for', e.target.value)}
                                    min={new Date().toISOString().slice(0, 16)}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('VideoConference.CreateModal.DateTimeHelp')}
                                </p>
                            </div>
                        )}

                        {/* Conference Settings */}
                        <div className="border-t pt-4">
                            <h3 className="text-md font-medium text-gray-900 mb-3">{t('VideoConference.CreateModal.SettingsTitle')}</h3>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t('VideoConference.CreateModal.MaxParticipantsLabel')}</label>
                                <select
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                                    value={formData.settings.max_participants}
                                    onChange={(e) => handleInputChange('settings.max_participants', parseInt(e.target.value))}
                                >
                                    <option value={5}>{t('VideoConference.CreateModal.MaxParticipants5')}</option>
                                    <option value={10}>{t('VideoConference.CreateModal.MaxParticipants10')}</option>
                                    <option value={25}>{t('VideoConference.CreateModal.MaxParticipants25')}</option>
                                    <option value={50}>{t('VideoConference.CreateModal.MaxParticipants50')}</option>
                                </select>
                            </div>

                            <div className="mt-4 space-y-3">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                                        checked={formData.settings.enable_chat}
                                        onChange={(e) => handleInputChange('settings.enable_chat', e.target.checked)}
                                    />
                                    <label className="ml-2 text-sm text-gray-700">{t('VideoConference.CreateModal.EnableChatLabel')}</label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                                        checked={formData.settings.enable_screen_share}
                                        onChange={(e) => handleInputChange('settings.enable_screen_share', e.target.checked)}
                                    />
                                    <label className="ml-2 text-sm text-gray-700">{t('VideoConference.CreateModal.EnableScreenShareLabel')}</label>
                                </div>
                            </div>
                        </div>


                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                            >
                                {t('VideoConference.CreateModal.CancelButton')}
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-black border border-transparent rounded-md hover:bg-gray-800 disabled:opacity-50"
                            >
                                {isLoading ? t('VideoConference.CreateModal.CreatingButton') : t('VideoConference.CreateModal.CreateButton')}
                            </button>
                        </div>
                    </form>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default ConferenceCreationModal;